import React from 'react';

import { useRecoilValue } from 'recoil';
import { PopoverPanel as HeadlessPopoverPanel } from '@headlessui/react';

import { ia } from 'lib/helpers/utility';

import Typography from 'components/shared/Typography/Typography';
import { permissions as permissionsState } from 'components/state';

import PopoverOptions from '../Options/PopoverOptions';
import { checkOptionPermission } from '../../lib/checkOptionPermission';

import { getPanelClasses } from './lib/getClasses';

const PopoverPanel = ({
  isFixed,
  handlePanelRef,
  panelRef,
  onScroll,
  panelClassName,
  position,
  panelFixedPosition,
  isPositioned,
  options,
  children,
  close,
  // Options props
  optionsGroupBy,
  optionGroupLabelClassName,
  hoveredIndexes,
  handleMouseEnter,
  handleMouseLeave,
  handleOptionClick,
  activeOptionRef,
  submenuRefs,
  text,
  customButton,
  optionIconSize,
  optionIconColor,
  iconsShade,
  optionArrowIcon,
  optionArrowIconStroke,
  optionActiveIcon,
  optionArrowColor,
  optionIconNoColor,
  onPanelMouseLeave,
  separatorClassName
}) => {
  const permissions = useRecoilValue(permissionsState);

  const renderOptions = (options, level = 1, handleClose, parentPath = '') => {
    const checkOptionPermissionFn = (opt) => checkOptionPermission(permissions)(opt);
    const visibleOptions = options.filter(checkOptionPermissionFn);

    let lastKeptWasSeparator = true;
    const filteredOptions = visibleOptions.filter((opt, index) => {
      const isSeparator = opt.type === 'separator' || opt.separator;

      if (!isSeparator) {
        lastKeptWasSeparator = false;
        return true;
      }

      const itemsAfter = visibleOptions.slice(index + 1);
      const hasNextNonSeparator = itemsAfter.some((o) => o.type !== 'separator' && !o.separator);

      if (!hasNextNonSeparator) return false;
      if (lastKeptWasSeparator) return false;

      lastKeptWasSeparator = true;
      return true;
    });

    if (!ia(filteredOptions)) {
      return (
        <Typography className="px-4 py-2" size="small" color="neutral-500">
          No options available
        </Typography>
      );
    }

    return (
      <PopoverOptions
        options={filteredOptions}
        level={level}
        handleClose={handleClose}
        parentPath={parentPath}
        optionsGroupBy={optionsGroupBy}
        optionGroupLabelClassName={optionGroupLabelClassName}
        hoveredIndexes={hoveredIndexes}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        handleOptionClick={handleOptionClick}
        activeOptionRef={activeOptionRef}
        submenuRefs={submenuRefs}
        text={text}
        customButton={customButton}
        optionIconSize={optionIconSize}
        optionIconColor={optionIconColor}
        iconsShade={iconsShade}
        optionArrowIcon={optionArrowIcon}
        optionArrowIconStroke={optionArrowIconStroke}
        optionActiveIcon={optionActiveIcon}
        optionArrowColor={optionArrowColor}
        position={position}
        renderOptions={renderOptions}
        optionIconNoColor={optionIconNoColor}
        separatorClassName={separatorClassName}
      />
    );
  };

  const renderChildren = (close) => {
    if (ia(options)) return renderOptions(options, 1, close);
    if (typeof children === 'function') {
      return (
        <div className="flex flex-col overflow-hidden">{children({ closePopover: close })}</div>
      );
    }
    if (children) {
      return <div className="flex flex-col overflow-hidden">{children}</div>;
    }
    return null;
  };

  return (
    <HeadlessPopoverPanel
      ref={isFixed ? handlePanelRef : panelRef}
      portal={isFixed}
      onScroll={onScroll}
      onMouseLeave={onPanelMouseLeave}
      className={getPanelClasses({ isFixed, position, options, panelClassName })}
      style={{
        ...(isFixed ? panelFixedPosition : {}),
        ...(isFixed && !isPositioned ? { visibility: 'hidden' } : {})
      }}>
      {renderChildren(close)}
    </HeadlessPopoverPanel>
  );
};

export default PopoverPanel;
